/* Shared pixel geometry; no product, price, cart, or network side effects. */
(function (root) {
  'use strict';
  const finitePositive = (n) => Number.isFinite(n) && n > 0;
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function dimensions(config) {
    const width = Number(config?.widthMm);
    const height = Number(config?.heightMm);
    if (!finitePositive(width) || !finitePositive(height)) throw new Error('Print dimensions are missing or invalid.');
    const bleed = config?.insetsMm || { top: 0, right: 0, bottom: 0, left: 0 };
    const insets = Object.fromEntries(['top', 'right', 'bottom', 'left'].map((key) => [key, Number(bleed[key] ?? 0)]));
    if (Object.values(insets).some((n) => !Number.isFinite(n) || n < 0) ||
        insets.left + insets.right >= width || insets.top + insets.bottom >= height) {
      throw new Error('The frame opening is invalid.');
    }
    return { widthMm: width, heightMm: height, insetsMm: insets };
  }

  function cropRect(imageWidth, imageHeight, ratio, zoom = 1, centerX = 0.5, centerY = 0.5) {
    if (![imageWidth, imageHeight, ratio, zoom].every(finitePositive) || zoom < 1 ||
        !Number.isFinite(centerX) || !Number.isFinite(centerY)) throw new Error('Invalid crop geometry.');
    const width = Math.min(imageWidth, imageHeight * ratio) / zoom;
    const height = width / ratio;
    return {
      x: clamp(centerX * imageWidth - width / 2, 0, imageWidth - width),
      y: clamp(centerY * imageHeight - height / 2, 0, imageHeight - height), width, height,
    };
  }

  function quality(rect, config, minimumPpi = 150, recommendedPpi = 300) {
    const size = dimensions(config);
    if (![rect.width, rect.height, minimumPpi, recommendedPpi].every(finitePositive)) {
      throw new Error('Invalid print quality configuration.');
    }
    const ppi = Math.min(rect.width * 25.4 / size.widthMm, rect.height * 25.4 / size.heightMm);
    return { ppi, status: ppi < recommendedPpi ? 'warning' : 'good' };
  }

  function pixelRect(rect, imageWidth, imageHeight) {
    const width = Math.max(1, Math.min(imageWidth, Math.floor(rect.width)));
    const height = Math.max(1, Math.min(imageHeight, Math.floor(rect.height)));
    return { x: clamp(Math.round(rect.x), 0, imageWidth - width), y: clamp(Math.round(rect.y), 0, imageHeight - height), width, height };
  }

  function resolveSize(productSize, variantSize) {
    // A partial variant override must not silently inherit the other dimension.
    return dimensions(variantSize == null ? productSize : variantSize);
  }

  function sizeKey(config) {
    const size = dimensions(config);
    return JSON.stringify([size.widthMm, size.heightMm, ...Object.values(size.insetsMm)]);
  }

  function pairedVariant(variants, selectedOptions, optionIndex, targetValue) {
    if (!Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex >= selectedOptions.length) return null;
    const options = selectedOptions.map((value, index) => index === optionIndex ? targetValue : value);
    // Exact configured values only; never guess from price or select a different tune/color.
    return variants.find((variant) => variant.options.length === options.length &&
      variant.options.every((value, index) => value === options[index])) || null;
  }

  const api = { clamp, dimensions, cropRect, pixelRect, quality, resolveSize, sizeKey, pairedVariant };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.OTSPhotoGeometry = api;
})(typeof window !== 'undefined' ? window : globalThis);
